/* eslint-disable react-refresh/only-export-components -- shared helpers live with the table components */
import {
  Button,
  Checkbox,
  Chip,
  Division,
  EmptyState,
  Flex,
  Heading,
  Icons,
  Markdown,
  Paragraph,
  Table,
  Tooltip,
  type TableColumnType,
  type TableColumnVerticalAlign,
  type TableProps,
  type TableRowSelectionType,
} from "@ir/ui";
import { useMemo } from "react";
import { useAutoHeight } from "@src/presentation/hooks/autoheight";

export const MASTER_DATA_COLUMN_VERTICAL_ALIGN: TableColumnVerticalAlign =
  "middle";

/** Safe cell text for Master Data tables (avoids `[object Object]`). */
export function formatMasterDataCellText(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "—";
  }
}

export function renderMasterDataTextCell(value: unknown) {
  return (
    <Paragraph className="m-0">{formatMasterDataCellText(value)}</Paragraph>
  );
}

export function renderMasterDataMarkdownCell(value: unknown) {
  if (typeof value !== "string" || value.trim() === "") {
    return (
      <Paragraph className="m-0">{formatMasterDataCellText(value)}</Paragraph>
    );
  }
  return (
    <Markdown content={value} className="master-data-help-text-markdown" />
  );
}

export function renderMasterDataHelpTextViewCell(
  value: unknown,
  onView: (text: string) => void,
  rowName?: string,
) {
  if (typeof value !== "string" || value.trim() === "") {
    return (
      <Paragraph className="m-0">{formatMasterDataCellText(value)}</Paragraph>
    );
  }
  const label = rowName
    ? `View help text for ${rowName}`
    : "View help text";
  return (
    <Flex align="center" justifyContent="start">
      <Button size="small" style={{ padding: "2px 8px", fontSize: "12px", letterSpacing: "1px" }} aria-label={label} onPress={() => onView(value)}>
        View
      </Button>
    </Flex>
  );
}

export function renderMasterDataStatusChip(value: unknown) {
  return (
    <Flex align="center" justifyContent="start">
      <Chip
        label={value ? "Active" : "Inactive"}
        color={value ? "success" : "error"}
        size="small"
      />
    </Flex>
  );
}

/**
 * `@ir/ui` Table only distributes column widths when `scroll.x` is a number,
 * so the total has to be derived from the column definitions.
 */
export function sumMasterDataColumnWidths<TRow>(
  columns: TableColumnType<TRow>[],
): number {
  return columns.reduce(
    (total, column) =>
      total + (typeof column.width === "number" ? column.width : 0),
    0,
  );
}

interface MasterDataTableEmptyStateProps {
  search: string;
  entityLabel: string;
  onClearSearch: () => void;
}

/** Shared empty / no-results UI for Master Data list tables. */
export function MasterDataTableEmptyState({
  search,
  entityLabel,
  onClearSearch,
}: Readonly<MasterDataTableEmptyStateProps>) {
  const trimmedSearch = search.trim();
  const hasActiveSearch = trimmedSearch.length > 0;

  if (hasActiveSearch) {
    return (
      <EmptyState
        className="px-3 py-4"
        icon={Icons.InfoCircle}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-2">
            No results found for &quot;{trimmedSearch}&quot;
          </Heading>
        }
        description={
          <>
            <Paragraph className="text-grey-500 font-regular mb-0">
              Try adjusting your search terms, or clear the search to see all{" "}
              {entityLabel}.
            </Paragraph>
            <Button className="mt-3" variant="clear" onPress={onClearSearch}>
              Clear search
            </Button>
          </>
        }
      />
    );
  }

  return (
    <EmptyState
      className="px-3 py-4"
      icon={Icons.Rows01}
      title={
        <Heading level={6} className="text-grey-700 font-bold mt-2">
          No data for this brand
        </Heading>
      }
      description={
        <Paragraph className="text-grey-500 font-regular mb-0">
          There is no master data available for the selected brand.
        </Paragraph>
      }
    />
  );
}

export const MASTER_DATA_LINKED_TOOLTIP = "Can't delete this item because it's linked to a deduction.";

export function renderMasterDataLinkedNameCell(
  value: unknown,
  record: { isLinked: boolean },
  tooltip: string = MASTER_DATA_LINKED_TOOLTIP,
) {
  const text = formatMasterDataCellText(value);
  if (!record.isLinked) {
    return <Paragraph className="m-0">{text}</Paragraph>;
  }
  return (
    <Tooltip content={tooltip}>
      <Button variant="clear" className="master-data-linked-name-trigger">
        {text}
      </Button>
    </Tooltip>
  );
}

export const MASTER_DATA_SELECTION_COLUMN_WIDTH = 55;

function MasterDataSelectionCheckbox({
  isDisabled = false,
  ...checkboxProps
}: Readonly<{
  isDisabled?: boolean;
  checked?: boolean;
  isIndeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  "aria-label": string;
}>) {
  return (
    <span className="master-data-selection-checkbox">
      <Checkbox
        color="grey"
        slot="selection"
        isDisabled={isDisabled}
        {...checkboxProps}
      />
    </span>
  );
}

interface MasterDataLinkedSelectionColumnArgs<
  TRow extends { key: string; isLinked: boolean },
> {
  selectedKeys: ReadonlySet<string>;
  selectableKeys: readonly string[];
  onChange: (selectedKeys: Set<string>) => void;
  getAriaLabel: (record: TRow) => string;
}

/** Selection column that disables checkboxes on linked rows instead of dimming the cell. */
export function createMasterDataLinkedSelectionColumn<
  TRow extends { key: string; isLinked: boolean },
>({
  selectedKeys,
  selectableKeys,
  onChange,
  getAriaLabel,
}: MasterDataLinkedSelectionColumnArgs<TRow>): TableColumnType<TRow> {
  const selectedSelectableCount = selectableKeys.filter((key) =>
    selectedKeys.has(key),
  ).length;
  const allSelectableSelected =
    selectableKeys.length > 0 &&
    selectedSelectableCount === selectableKeys.length;
  const someSelectableSelected =
    selectedSelectableCount > 0 && !allSelectableSelected;

  return {
    title:
      selectableKeys.length === 0 ? (
        ""
      ) : (
        <MasterDataSelectionCheckbox
          checked={allSelectableSelected}
          isIndeterminate={someSelectableSelected}
          onChange={(checked) => {
            onChange(checked ? new Set(selectableKeys) : new Set());
          }}
          aria-label="Select all unlinked rows"
        />
      ),
    dataIndex: "key",
    key: "selection",
    align: "center",
    verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
    width: MASTER_DATA_SELECTION_COLUMN_WIDTH,
    fixed: "left",
    cellRender: (_value: unknown, record: TRow) => (
      <MasterDataSelectionCheckbox
        isDisabled={record.isLinked}
        checked={selectedKeys.has(record.key)}
        onChange={(checked) => {
          if (record.isLinked) return;
          const next = new Set(selectedKeys);
          if (checked) next.add(record.key);
          else next.delete(record.key);
          onChange(next);
        }}
        aria-label={getAriaLabel(record)}
      />
    ),
  };
}

interface MasterDataListTableProps<TRow extends { key: string }> {
  columns: TableColumnType<TRow>[];
  dataSource: TRow[];
  search: string;
  entityLabel: string;
  ariaLabel: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  rowSelection?: TableRowSelectionType<TRow>;
  rowClassName?: NonNullable<TableProps<TRow>["rowClassName"]>;
}

/** Shared scrollable table shell for Master Data list pages. */
export function MasterDataListTable<TRow extends { key: string }>({
  columns,
  dataSource,
  search,
  entityLabel,
  ariaLabel,
  setSearch,
  setPage,
  rowSelection,
  rowClassName,
}: Readonly<MasterDataListTableProps<TRow>>) {
  const { ref: tableScrollContainerRef, height: tableHeight } =
    useAutoHeight<HTMLDivElement>();
  const tableWidth = useMemo(() => {
    const columnsWidth = sumMasterDataColumnWidths(columns);
    if (!rowSelection) {
      return columnsWidth;
    }
    const selectionWidth =
      typeof rowSelection.columnWidth === "number"
        ? rowSelection.columnWidth
        : MASTER_DATA_SELECTION_COLUMN_WIDTH;
    return columnsWidth + selectionWidth;
  }, [columns, rowSelection]);

  const clearSearch = () => {
    setPage(1);
    setSearch("");
  };

  return (
    <Division
      ref={tableScrollContainerRef}
      style={{ flex: 1, minHeight: 0, minWidth: 0 }}
    >
      {dataSource.length === 0 ? (
        <MasterDataTableEmptyState
          search={search}
          entityLabel={entityLabel}
          onClearSearch={clearSearch}
        />
      ) : (
        <Table<TRow>
          columns={columns}
          dataSource={dataSource}
          rowHoverable
          rowSelection={rowSelection}
          rowClassName={rowClassName}
          aria-label={ariaLabel}
          sticky
          scroll={{ x: tableWidth, y: tableHeight }}
        />
      )}
    </Division>
  );
}
